/**
 * Word-level diff for progress note lines: split "Label: answer", Myers-style LCS diff,
 * and character-level prefix/suffix refinement for tracked-changes UI.
 */

export interface TitleAndAnswer {
  title: string;
  answer: string;
}

export function splitFieldTitleFromLine(line: string): TitleAndAnswer {
  const idx = line.indexOf(': ');
  if (idx < 0) {
    return { title: '', answer: line.trim() };
  }
  return {
    title: `${line.slice(0, idx).trim()}:`,
    answer: line.slice(idx + 2).trim(),
  };
}

export type AnswerDiffSegment = {
  kind: 'same' | 'removed' | 'added';
  text: string;
  tightAfterPrev?: boolean;
};

function longestCommonPrefixLen(a: string, b: string): number {
  let i = 0;
  const max = Math.min(a.length, b.length);
  while (i < max && a[i] === b[i]) i += 1;
  return i;
}

function longestCommonSuffixLen(a: string, b: string): number {
  let i = 0;
  const max = Math.min(a.length, b.length);
  while (i < max && a[a.length - 1 - i] === b[b.length - 1 - i]) {
    i += 1;
  }
  return i;
}

// Minimum shared characters required before we split a word-pair at the character level.
// Below this threshold (e.g. "Office"→"Home" share only 1 trailing 'e') the refined output
// looks confusing ("Offic Hom e"), so we fall back to showing the words as full replacements.
const MIN_MEANINGFUL_COMMON = 3;

function refineRemovedAddedPair(
  removed: string,
  added: string,
): AnswerDiffSegment[] {
  if (!removed) return added ? [{ kind: 'added', text: added }] : [];
  if (!added) return [{ kind: 'removed', text: removed }];

  const plen = longestCommonPrefixLen(removed, added);
  const rMid = removed.slice(plen);
  const aMid = added.slice(plen);
  const slen = longestCommonSuffixLen(rMid, aMid);

  // If neither the shared prefix nor the shared suffix is meaningful, treat the
  // pair as a straight whole-word replacement — avoids artifacts like "Offic Hom e".
  if (plen < MIN_MEANINGFUL_COMMON && slen < MIN_MEANINGFUL_COMMON) {
    const out: AnswerDiffSegment[] = [];
    if (removed) out.push({ kind: 'removed', text: removed });
    if (added) out.push({ kind: 'added', text: added });
    return out;
  }

  const rCore = rMid.slice(0, rMid.length - slen);
  const aCore = aMid.slice(0, aMid.length - slen);
  const suf = slen > 0 ? rMid.slice(rMid.length - slen) : '';

  const out: AnswerDiffSegment[] = [];
  if (plen > 0) out.push({ kind: 'same', text: removed.slice(0, plen) });
  if (rCore) out.push({ kind: 'removed', text: rCore, tightAfterPrev: out.length > 0 });
  if (aCore) out.push({ kind: 'added', text: aCore, tightAfterPrev: out.length > 0 });
  if (suf) out.push({ kind: 'same', text: suf, tightAfterPrev: out.length > 0 });
  return out;
}

function mergeTrailingSame(out: AnswerDiffSegment[], seg: AnswerDiffSegment): void {
  if (seg.tightAfterPrev) {
    out.push({ ...seg });
    return;
  }
  const last = out[out.length - 1];
  if (last?.kind === 'same' && seg.kind === 'same' && !last.tightAfterPrev) {
    const before = last.text;
    const after = seg.text;
    if (/\s$/.test(before) || /^\s/.test(after)) {
      last.text = `${before}${after}`;
    } else {
      last.text = `${before} ${after}`;
    }
  } else {
    out.push({ ...seg });
  }
}

function refineDiffSegments(segments: AnswerDiffSegment[]): AnswerDiffSegment[] {
  const out: AnswerDiffSegment[] = [];
  let i = 0;
  while (i < segments.length) {
    const seg = segments[i];
    if (seg.kind === 'removed') {
      const addedParts: string[] = [];
      let j = i + 1;
      while (j < segments.length && segments[j].kind === 'added') {
        addedParts.push(segments[j].text);
        j += 1;
      }
      if (addedParts.length > 0) {
        const combinedAdded = addedParts.join(' ');
        const refined = refineRemovedAddedPair(seg.text, combinedAdded);
        for (const r of refined) {
          mergeTrailingSame(out, r);
        }
        i = j;
        continue;
      }
    }
    mergeTrailingSame(out, seg);
    i += 1;
  }
  return out;
}

/**
 * Myers-style LCS word diff. Only differing runs are styled; unchanged prose stays normal.
 */
export function diffAnswerWords(oldAnswer: string, newAnswer: string): AnswerDiffSegment[] {
  const a = oldAnswer.trim().split(/\s+/).filter(Boolean);
  const b = newAnswer.trim().split(/\s+/).filter(Boolean);
  const n = a.length;
  const m = b.length;
  if (n === 0 && m === 0) return [];
  if (n === 0) {
    return m ? [{ kind: 'added', text: b.join(' ') }] : [];
  }
  if (m === 0) {
    return [{ kind: 'removed', text: a.join(' ') }];
  }

  const dp: number[][] = Array.from({ length: n + 1 }, () => Array(m + 1).fill(0));
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      dp[i][j] =
        a[i - 1] === b[j - 1]
          ? dp[i - 1][j - 1] + 1
          : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }

  const raw: Array<{ t: 'eq' | 'del' | 'ins'; w: string }> = [];
  let i = n;
  let j = m;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && a[i - 1] === b[j - 1]) {
      raw.unshift({ t: 'eq', w: a[i - 1] });
      i -= 1;
      j -= 1;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      raw.unshift({ t: 'ins', w: b[j - 1] });
      j -= 1;
    } else {
      raw.unshift({ t: 'del', w: a[i - 1] });
      i -= 1;
    }
  }

  const kind = (t: 'eq' | 'del' | 'ins'): AnswerDiffSegment['kind'] =>
    t === 'eq' ? 'same' : t === 'del' ? 'removed' : 'added';

  const merged: AnswerDiffSegment[] = [];
  for (const r of raw) {
    const k = kind(r.t);
    const last = merged[merged.length - 1];
    if (last && last.kind === k) {
      last.text = `${last.text} ${r.w}`;
    } else {
      merged.push({ kind: k, text: r.w });
    }
  }
  return refineDiffSegments(merged);
}
